import { observer } from 'mobx-react';

import Icon from '../../../app/shared/components/Icon/Icon';
import InputSearch from '../../../app/shared/components/InputSearch/InputSearch';
import ThreeDotsLoader from '../../../app/shared/components/Loaders/ThreeDotsLoader';
import ResultsCounter from '../../../app/shared/components/ResultsCounter/ResultsCounter';
import { PricelistProductTranslation } from '../../localization/ModuleTranslation';
import ConfirmationModal from '../../shared/ConfirmationModal/ConfirmationModal';
import ExportPricelistToExcelModal from '../../shared/ExportPricelistToExcelModal/ExportPricelistToExcelModal';
import ExportPricelistToPdfModal from '../../shared/ExportPricelistToPdfModal/ExportPricelistToPdfModal';
import PricelistFormModal from '../../shared/PricelistFormModal/PricelistFormModal';
import Button from '../../ui/Button/Button';
import DateTime from '../../ui/DateTime';
import LoadingStatus from '../../ui/LoadingStatus/LoadingStatus';
import LoadMore from '../../ui/LoadMore/LoadMore';
import { Menu, MenuButton, MenuContent, MenuItem } from '../../ui/Menu/Menu';
import { Backlink, TopBar, TopBarActions } from '../../ui/TopBar/TopBar';
import css from './PricelistDetails.css';
import PricelistDetailsState from './PricelistDetailsState';
import { PricelistProduct, PricelistProductSkeleton } from './PricelistProduct/PricelistProduct';

interface PricelistDetailsProps {
  state: PricelistDetailsState;
}

const PricelistDetails = observer(({ state }: PricelistDetailsProps) => {
  const { data, translation } = state;

  const isLoadingInitialData = state.loadingState === 'loading';
  const isLoading = isLoadingInitialData || state.loadingState === 'loadingMore';

  return (
    <main className={css.root}>
      <TopBar className={css.topBar}>
        <Backlink to={state.backUrl}>{translation.backlinkLabel}</Backlink>
        <TopBarActions>
          <ExportPricelistToExcelModal
            state={state.exportToExcelModal}
            translation={translation.exportToExcelModal}
            trigger={
              <Button size="medium" theme="primary" variant="outline">
                <Icon className={css.exportIcon} name="filePlus" /> {translation.exportLabel}
              </Button>
            }
          />
          <ExportPricelistToPdfModal
            state={state.exportToPdfModal}
            translation={translation.exportToPdfModal}
            trigger={
              <Button size="medium" theme="primary" variant="outline">
                <Icon className={css.downloadIcon} name="downloadLight" /> {translation.downloadLabel}
              </Button>
            }
          />
        </TopBarActions>
      </TopBar>
      <div className={css.mainContent}>
        <header className={css.header}>
          <div>
            <h1 className={css.heading}>{data.name}</h1>
            {data.description && <p className={css.description}>{data.description}</p>}
          </div>
          <div className={css.details}>
            <dl className={css.metadata}>
              <div>
                <dt className={css.metadataLabel}>{translation.itemsLabel}</dt>
                <dd className={css.metadataValue}>{data.totalProducts}</dd>
              </div>
              <div>
                <dt className={css.metadataLabel}>{translation.createdAtLabel}</dt>
                <dd className={css.metadataValue}>
                  <DateTime value={data.createdAt} locale={state.locale} format="date-only" />
                </dd>
              </div>
              <div>
                <dt className={css.metadataLabel}>{translation.modifiedAtLabel}</dt>
                <dd className={css.metadataValue}>
                  <DateTime value={data.modifiedAt} locale={state.locale} format="date-only" />
                </dd>
              </div>
            </dl>
            <Menu>
              <MenuButton className={css.settingsButton}>
                <Icon className={css.icon} name="settings" />
                {translation.settingsLabel}
              </MenuButton>
              <MenuContent offset={2}>
                <MenuItem onAction={() => state.editPricelist()}>{translation.pricelistActions.edit.label}</MenuItem>
                <MenuItem onAction={() => state.deletePricelist()}>
                  {translation.pricelistActions.delete.label}
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        </header>
        <dl className={css.context}>
          <div>
            <dt className={css.metadataLabel}>{translation.regionLabel}</dt>
            <dd className={css.metadataValue}>{state.region}</dd>
          </div>
          <div>
            <dt className={css.metadataLabel}>{translation.currencyLabel}</dt>
            <dd className={css.metadataValue}>
              {translation.currencyExcludingVatFormat.interpolate([['currency', state.currency]])}
            </dd>
          </div>
          {/* TODO: Commented out until `GetCustomerPricelistMetadataQuery` carries the refresh time. */}
          {/* <div>
            <dt className={css.metadataLabel}>{translation.pricesRefreshedAtLabel}</dt>
            <dd className={css.metadataValue}>
              <DateTime value={data.pricesRefreshedAt} locale={state.locale} />
            </dd>
          </div> */}
        </dl>
        <div className={css.toolbar}>
          <search className={css.search}>
            <InputSearch
              defaultValue={state.searchTerm}
              placeholderTranslation={translation.searchPlaceholder}
              onValueChange={state.setSearchTerm}
            />
          </search>
          <ResultsCounter translation={translation.itemsFoundLabel} value={state.total} />
        </div>
        <section aria-label={translation.productsLabel}>
          {isLoading && (
            <LoadingStatus message={isLoadingInitialData ? translation.loadingLabel : translation.loadingMoreLabel} />
          )}
          {isLoadingInitialData ? (
            <PricelistProductListSkeleton translation={translation.product} />
          ) : state.items.empty() ? (
            <p className={css.empty}>{translation.emptyText}</p>
          ) : (
            <>
              <ul className={css.list}>
                {state.items.map(({ data: item, accessories }) => (
                  <li key={item.id}>
                    <PricelistProduct
                      product={item.product}
                      accessories={accessories}
                      translation={translation.product}
                      actions={[
                        { label: translation.productActions.remove.label, action: () => state.removeItem(item) },
                      ]}
                    />
                  </li>
                ))}
              </ul>
              {state.canLoadMoreItems && (
                <LoadMore
                  isLoading={state.loadingState === 'loadingMore'}
                  scrollOffset={0.5}
                  onLoadMore={state.loadMoreItems}
                >
                  <div className={css.loadingMore}>
                    <ThreeDotsLoader />
                  </div>
                </LoadMore>
              )}
            </>
          )}
        </section>
      </div>
      <PricelistFormModal state={state.editPricelistModal} />
      <ConfirmationModal state={state.confirmationModal} />
    </main>
  );
});

export default PricelistDetails;

/** Placeholder products for a load from the first page: enough to fill the fold. */
const SKELETON_PRODUCT_COUNT = 3;

interface PricelistProductListSkeletonProps {
  translation: PricelistProductTranslation;
}

const PricelistProductListSkeleton = ({ translation }: PricelistProductListSkeletonProps) => {
  return (
    <div className={css.list}>
      {Array.from({ length: SKELETON_PRODUCT_COUNT }, (_: unknown, index: number) => (
        <PricelistProductSkeleton key={index} translation={translation} />
      ))}
    </div>
  );
};
